/**
 * Menu de gauche de l'espace paroisse, calqué sur le manuel 2 « L'espace de
 * votre paroisse » (page 6, « Le menu de gauche ») : quatorze sections, dans
 * cet ordre et sous ces noms. Décision PO du 09/10/2026 : l'écran correspond
 * au manuel ; ce menu REMPLACE le menu court « L'essentiel / Plus » du matin.
 *
 *   Dashboard · Paroissiens · Sacrements · Pastorale · Finances · Patrimoine ·
 *   Communication · Secrétariat · Gouvernance · Mouvements · Événements ·
 *   Mesure · Formation · Mission
 *
 * Chaque entrée reste filtrée par les droits de `role-permissions` : une
 * section dont aucune entrée n'est ouverte au rôle disparaît (« le menu ne
 * montre que les sections ouvertes à votre rôle »). Le curé voit tout.
 *
 * Le manuel range deux écrans dans deux sections à la fois : la catéchèse
 * (Pastorale et Formation) et les commissions (Gouvernance et Mouvements).
 * La seconde occurrence porte un testid suffixé par la clé de section.
 *
 * Vitrines « à venir » : celles que le manuel cite (Impressions, Réseaux
 * sociaux, Système qualité, Laboratoire d'innovation, Recherche appliquée)
 * gardent leur entrée, marquée « À venir », et ouvrent une page « à venir »
 * claire (voir App.tsx). Les autres restent hors menu.
 *
 * i18n-dynamic: common.sidebarGroups.*
 * i18n-dynamic: finance.guichet.menu (page 91, C2 du 19/10/2026)
 */
import type { ElementType } from "react";
import {
  Users, Church, Heart, HandCoins, Building2, MessageSquare, Briefcase,
  Crown, Flag, Calendar, BarChart3, GraduationCap, Globe,
} from "lucide-react";
import { getAccessiblePages } from "@/lib/role-permissions";

export type MenuEntry = {
  id: number;
  labelKey: string;
  /** Testid de la seconde occurrence d'un écran rangé dans deux sections. */
  testId?: string;
  /** Vitrine citée par le manuel : entrée gardée, page « à venir ». */
  comingSoon?: boolean;
};

export type MenuSection = {
  key: string;
  labelKey: string;
  icon: ElementType;
  color: string;
  entries: MenuEntry[];
};

const K = "common.sidebarGroups.";

/** Tableaux de bord complémentaires, rangés sous l'entrée « Dashboard ». */
export const DASHBOARD_ENTRIES: MenuEntry[] = [
  { id: 73, labelKey: `${K}dashboardEconome` },
  { id: 74, labelKey: `${K}dashboardCPAE` },
  { id: 78, labelKey: `${K}dashboardVicaire` },
];

export const MENU_SECTIONS: MenuSection[] = [
  {
    key: "paroissiens", labelKey: `${K}paroissiens`, icon: Users, color: "var(--brand-blue)",
    entries: [
      { id: 3, labelKey: `${K}annuaireParoissial` },
      // Le profil (4) s'ouvre depuis une ligne de l'annuaire, pas du menu.
      { id: 5, labelKey: `${K}segmentation` },
    ],
  },
  {
    key: "sacrements", labelKey: `${K}sacrements`, icon: Church, color: "var(--brand-gold)",
    entries: [
      { id: 6, labelKey: `${K}registresSacramentels` },
      { id: 7, labelKey: `${K}intentionsMesse` },
      { id: 80, labelKey: `${K}preparationMariage` },
      { id: 30, labelKey: `${K}funerailles` },
      { id: 31, labelKey: `${K}cimetiere` },
    ],
  },
  {
    key: "pastorale", labelKey: `${K}pastorale`, icon: Heart, color: "var(--brand-mat-indigo)",
    entries: [
      { id: 79, labelKey: `${K}planningLiturgique` },
      { id: 92, labelKey: `${K}horairesMesses` },
      { id: 82, labelKey: `${K}groupesPriere` },
      { id: 83, labelKey: `${K}visitesMalades` },
      { id: 41, labelKey: `${K}catechese` },
      { id: 84, labelKey: `${K}aidesSociales` },
    ],
  },
  {
    key: "finances", labelKey: `${K}finances`, icon: HandCoins, color: "var(--brand-orange)",
    entries: [
      { id: 8, labelKey: `${K}cockpitFinancier` },
      // « Ma semaine » (proposition du 02/11/2026, D5) : toute la semaine sur un
      // écran, à côté des pages actuelles, qui restent. Allumé par environnement.
      { id: 93, labelKey: `${K}maSemaine` },
      { id: 77, labelKey: `${K}collecteDons` },
      // C2 (19/10/2026) : la secrétaire encaisse au guichet (troncs, denier,
      // dîmes, dons, casuels, kiosque, catéchèse, location). Libellé rangé
      // avec les textes du guichet, sous finance.guichet.
      { id: 91, labelKey: "finance.guichet.menu" },
      { id: 9, labelKey: `${K}clotureHebdomadaire` },
      { id: 75, labelKey: `${K}comptabilite` },
      { id: 40, labelKey: `${K}reporting` },
      { id: 81, labelKey: `${K}mobileMoney` },
      { id: 90, labelKey: `${K}reversements` },
      { id: 68, labelKey: `${K}registreDonateurs` },
    ],
  },
  {
    key: "patrimoine", labelKey: `${K}patrimoine`, icon: Building2, color: "var(--brand-mat-brown)",
    entries: [
      { id: 27, labelKey: `${K}batiment` },
      { id: 28, labelKey: `${K}inventaire` },
      { id: 71, labelKey: `${K}stocks` },
      { id: 29, labelKey: `${K}vehicules` },
      { id: 69, labelKey: `${K}gestionPatrimoine` },
      { id: 70, labelKey: `${K}inventaireDetail` },
      { id: 23, labelKey: `${K}logistique` },
      { id: 35, labelKey: `${K}jardins` },
      { id: 32, labelKey: `${K}securite` },
      { id: 22, labelKey: `${K}catalogue` },
      { id: 21, labelKey: `${K}boutique` },
    ],
  },
  {
    key: "communication", labelKey: `${K}communication`, icon: MessageSquare, color: "var(--brand-violet)",
    entries: [
      { id: 10, labelKey: `${K}hubCommunication` },
      { id: 11, labelKey: `${K}siteWeb` },
      { id: 48, labelKey: `${K}reseauxSociaux`, comingSoon: true },
      { id: 47, labelKey: `${K}centreMedia` },
      { id: 87, labelKey: `${K}portailParoissien` },
      { id: 89, labelKey: `${K}guideParoissien` },
      // Assistant IA « Padre » (12) retiré du menu avant le lancement (audit
      // de la boucle financière du 20/10/2026, segment 8) : il donnait de
      // fausses règles sur l'argent. La route et les droits restent ; la
      // page porte un bandeau qui renvoie à Assistance.
    ],
  },
  {
    key: "secretariat", labelKey: `${K}secretariat`, icon: Briefcase, color: "var(--brand-gold)",
    entries: [
      { id: 67, labelKey: `${K}courrier` },
      { id: 66, labelKey: `${K}agendaCure` },
      { id: 36, labelKey: `${K}archives` },
      { id: 38, labelKey: `${K}impressions`, comingSoon: true },
      { id: 39, labelKey: `${K}juridique` },
    ],
  },
  {
    key: "gouvernance", labelKey: `${K}gouvernance`, icon: Crown, color: "var(--brand-royal-light)",
    entries: [
      { id: 62, labelKey: `${K}conseilPastoral` },
      { id: 63, labelKey: `${K}conseilEconomique` },
      { id: 64, labelKey: `${K}equipeComptage` },
      { id: 14, labelKey: `${K}reunionsConseil` },
      { id: 65, labelKey: `${K}commissions` },
      { id: 61, labelKey: `${K}clerge` },
      { id: 15, labelKey: `${K}rh` },
      { id: 88, labelKey: `${K}rapportDiocese` },
      { id: 57, labelKey: `${K}supervision` },
      { id: 18, labelKey: `${K}urgences` },
      { id: 33, labelKey: `${K}crises` },
      { id: 76, labelKey: `${K}parametres` },
    ],
  },
  {
    key: "mouvements", labelKey: `${K}mouvements`, icon: Flag, color: "var(--brand-orange)",
    entries: [
      { id: 45, labelKey: `${K}portailMouvements` },
      { id: 46, labelKey: `${K}coordinationDiocesaine` },
      { id: 65, labelKey: `${K}commissions`, testId: "sidebar-page-65-mouvements" },
    ],
  },
  {
    key: "evenements", labelKey: `${K}evenements`, icon: Calendar, color: "var(--brand-mat-cyan-dark)",
    entries: [
      { id: 24, labelKey: `${K}calendrierMaitre` },
      { id: 25, labelKey: `${K}evenementsListe` },
      { id: 85, labelKey: `${K}kermesse` },
      { id: 26, labelKey: `${K}reservations` },
    ],
  },
  {
    key: "mesure", labelKey: `${K}mesure`, icon: BarChart3, color: "var(--brand-navy)",
    entries: [
      { id: 52, labelKey: `${K}analyticsPastoral` },
      { id: 13, labelKey: `${K}analyticsPredictifs` },
      { id: 58, labelKey: `${K}qualite`, comingSoon: true },
      { id: 59, labelKey: `${K}retoursFideles` },
      { id: 60, labelKey: `${K}capitalisation` },
      { id: 54, labelKey: `${K}laboInnovation`, comingSoon: true },
      { id: 55, labelKey: `${K}rechercheAppliquee`, comingSoon: true },
    ],
  },
  {
    key: "formation", labelKey: `${K}formation`, icon: GraduationCap, color: "var(--brand-mat-indigo)",
    entries: [
      { id: 41, labelKey: `${K}catechese`, testId: "sidebar-page-41-formation" },
      { id: 42, labelKey: `${K}preparationSacrements` },
      { id: 43, labelKey: `${K}formationAdultes` },
      { id: 44, labelKey: `${K}bibliotheque` },
      { id: 20, labelKey: `${K}formationGenerale` },
    ],
  },
  {
    key: "mission", labelKey: `${K}mission`, icon: Globe, color: "var(--brand-success)",
    entries: [
      { id: 50, labelKey: `${K}jumelages` },
      { id: 51, labelKey: `${K}missions` },
      { id: 53, labelKey: `${K}vocations` },
    ],
  },
];

/**
 * Écrans gardés (route et droits de role-permissions inchangés) mais retirés
 * de toute navigation : menu, explorateur des modules de l'accueil.
 *  - 12 : assistant IA « Padre », jusqu'à ce qu'il connaisse l'application
 *    (audit du 20/10/2026, segment 8).
 */
export const ECRANS_HORS_NAVIGATION: ReadonlySet<number> = new Set([12]);

/** Ordre affiché des quatorze sections du manuel (Dashboard en tête). */
export const MANUAL_SECTION_ORDER = ["dashboard", ...MENU_SECTIONS.map((s) => s.key)] as const;

/**
 * Sections et entrées visibles pour un rôle (droits de role-permissions),
 * sans les écrans éteints dans cet environnement (`masquees`).
 */
export function getVisibleSections(role: string, masquees: ReadonlySet<number> = new Set()): MenuSection[] {
  const accessible = getAccessiblePages(role);
  return MENU_SECTIONS
    .map((s) => ({ ...s, entries: s.entries.filter((e) => accessible.includes(e.id) && !masquees.has(e.id)) }))
    .filter((s) => s.entries.length > 0);
}

/** Tableaux de bord complémentaires visibles pour un rôle. */
export function getVisibleDashboards(role: string): MenuEntry[] {
  const accessible = getAccessiblePages(role);
  return DASHBOARD_ENTRIES.filter((e) => accessible.includes(e.id));
}

/**
 * Titre (clé i18n) de chaque vitrine « à venir » : App.tsx leur fait ouvrir
 * la page « à venir » claire plutôt qu'une coquille remplie de « · ».
 */
export const COMING_SOON_TITLE_KEYS: Readonly<Record<number, string>> = {
  16: `${K}realiteAugmentee`,
  17: `${K}domotique`,
  19: `${K}innovation`,
  34: `${K}energie`,
  37: `${K}administrationGen`,
  38: `${K}impressions`,
  48: `${K}reseauxSociaux`,
  49: `${K}sitewebAvance`,
  54: `${K}laboInnovation`,
  55: `${K}rechercheAppliquee`,
  56: `${K}universiteClerge`,
  58: `${K}qualite`,
};

/** Toutes les pages présentes dans le menu (pour les tests de couverture). */
export function getMenuPageIds(): number[] {
  const ids = new Set<number>([2, 86]);
  DASHBOARD_ENTRIES.forEach((e) => ids.add(e.id));
  MENU_SECTIONS.forEach((s) => s.entries.forEach((e) => ids.add(e.id)));
  return Array.from(ids).sort((a, b) => a - b);
}
